import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Container,
  Form,
  Row,
  Col,
  Button,
  Dropdown,
  ToggleButton,
  ToggleButtonGroup
} from "react-bootstrap";
import {
  RefreshCw,
  Bookmark,
  X,
  Flame,
  History,
  SlidersHorizontal
} from "lucide-react";
import { Title } from "../components/common/Title";
import MarketTable from "../components/market/MarketTable";
import MarketTableSkeleton from "../components/market/MarketTableSkeleton";
import type {
  MarketListing,
  MarketSummary,
  MarketView
} from "../types/market";
import type { PaginationMetadata } from "../types/boards";
import { MARKET_SLOTS } from "../constants/marketSlots";
import { getDevLiveListings } from "../constants/marketDevListings";

const REFRESH_COOLDOWN_MS = 60 * 1000;
const LIVE_LIMIT = 200;
// Past this, the plugin has probably stopped syncing and Live is out of date.
const STALE_SYNC_MS = 30 * 60 * 1000;

type Outcome = "sold" | "expired" | "both";
type Period = "" | "24h" | "7d" | "30d";

const LIVE_SORTS = [
  { value: "listedAt", label: "Newest" },
  { value: "endingSoon", label: "Ending soon" },
  { value: "highestBid", label: "Highest bid" },
  { value: "lowestBuyout", label: "Lowest buyout" }
] as const;

const HISTORY_SORTS = [
  { value: "concludedDesc", label: "Latest" },
  { value: "concludedAsc", label: "Oldest" },
  { value: "priceDesc", label: "Price ↓" },
  { value: "priceAsc", label: "Price ↑" }
] as const;

const DEFAULT_SORT: Record<MarketView, string> = {
  live: "listedAt",
  history: "concludedDesc"
};

const sortsFor = (view: MarketView): readonly { value: string; label: string }[] =>
  view === "live" ? LIVE_SORTS : HISTORY_SORTS;

interface FilterState {
  view: MarketView;
  q: string;
  seller: string;
  buyer: string;
  category: string;
  slot: string;
  minLevel: string;
  maxLevel: string;
  // "" means the view's default sort.
  sort: string;
  buyoutOnly: "" | "true";
  outcome: Outcome;
  period: Period;
}

// Defaults are left out of the URL, so Live (the default view) has no
// `view` param and History is `view=history`.
const DEFAULT_FILTERS: FilterState = {
  view: "live",
  q: "",
  seller: "",
  buyer: "",
  category: "",
  slot: "",
  minLevel: "",
  maxLevel: "",
  sort: "",
  buyoutOnly: "",
  outcome: "sold",
  period: ""
};

const EMPTY_PAGINATION: PaginationMetadata = {
  currentPage: 1,
  totalPages: 1,
  totalCount: 0,
  limit: 25,
  offset: 0,
  hasNextPage: false,
  hasPreviousPage: false
};

const FILTER_KEYS = Object.keys(DEFAULT_FILTERS) as (keyof FilterState)[];

// Before the Live/History split, the page had a Listed/Sold/Expired/All
// status toggle. Old links and saved searches map onto the new views.
const migrateLegacyStatus = (
  filters: FilterState,
  status: string | undefined
): FilterState => {
  if (status === "sold" || status === "expired") {
    return { ...filters, view: "history", outcome: status };
  }
  if (status === "all" || status === "") {
    return { ...filters, view: "history", outcome: "both" };
  }
  return filters;
};

const sanitize = (filters: FilterState): FilterState => ({
  ...filters,
  view: filters.view === "history" ? "history" : "live",
  sort: sortsFor(filters.view === "history" ? "history" : "live").some(
    s => s.value === filters.sort
  )
    ? filters.sort
    : ""
});

const filtersFromSearchParams = (params: URLSearchParams): FilterState => {
  let filters = { ...DEFAULT_FILTERS };
  for (const key of FILTER_KEYS) {
    const value = params.get(key);
    if (value) filters[key] = value as never;
  }
  if (!params.get("view")) {
    filters = migrateLegacyStatus(filters, params.get("status") ?? undefined);
  }
  return sanitize(filters);
};

interface SavedSearch {
  name: string;
  filters: FilterState;
}

const SAVED_SEARCHES_KEY = "market_saved_searches";

const loadSavedSearches = (): SavedSearch[] => {
  try {
    const stored = localStorage.getItem(SAVED_SEARCHES_KEY);
    const saved: (SavedSearch & { filters: { status?: string } })[] = stored
      ? JSON.parse(stored)
      : [];
    return saved.map(s => {
      const merged = { ...DEFAULT_FILTERS, ...s.filters } as FilterState;
      const filters = s.filters.view
        ? merged
        : migrateLegacyStatus(merged, s.filters.status ?? "");
      return { name: s.name, filters: sanitize(filters) };
    });
  } catch {
    return [];
  }
};

// The local DB lags prod, so when pointed at a local API the Live view also
// shows a few hardcoded listings (see constants/marketDevListings.ts).
const IS_LOCAL_API =
  process.env.NODE_ENV === "development" ||
  /localhost|127\.0\.0\.1/.test(process.env.REACT_APP_API ?? "");

const withDevListings = (
  fetched: MarketListing[],
  filters: FilterState,
  sort: string
): MarketListing[] => {
  const q = filters.q.toLowerCase();
  const seller = filters.seller.toLowerCase();
  const dev = getDevLiveListings().filter(
    l =>
      (!q || (l.itemNamePlain ?? "").toLowerCase().includes(q)) &&
      (!seller || (l.seller ?? "").toLowerCase().includes(seller)) &&
      (!filters.category || l.category === filters.category) &&
      !filters.slot &&
      (!filters.minLevel || (l.level ?? 0) >= Number(filters.minLevel)) &&
      (!filters.maxLevel || (l.level ?? 0) <= Number(filters.maxLevel)) &&
      ((!filters.buyoutOnly && sort !== "lowestBuyout") || !!l.buyoutPrice)
  );
  const merged = [...dev, ...fetched];
  if (sort === "endingSoon") {
    merged.sort(
      (a, b) =>
        new Date(a.endsAt ?? 0).getTime() - new Date(b.endsAt ?? 0).getTime()
    );
  }
  return merged;
};

const formatCompact = (value: number) =>
  value < 1_000_000
    ? value.toLocaleString()
    : new Intl.NumberFormat("en-US", {
        notation: "compact",
        maximumFractionDigits: 1
      }).format(value);

const formatAgo = (from: number, now: number) => {
  const minutes = Math.max(0, Math.floor((now - from) / 60000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

interface ChoiceGroupProps<T extends string> {
  label: string;
  name: string;
  value: T;
  options: readonly { value: T; label: string }[];
  onChange: (value: T) => void;
}

// Radio group rendered as square buttons - used instead of dropdowns.
function ChoiceGroup<T extends string>({
  label,
  name,
  value,
  options,
  onChange
}: ChoiceGroupProps<T>) {
  return (
    <Form.Group className="market-choice-group">
      <Form.Label className="d-block">{label}</Form.Label>
      <ToggleButtonGroup
        type="radio"
        name={name}
        value={value}
        onChange={(next: T) => onChange(next)}
        className="market-choice-buttons"
      >
        {options.map(option => (
          <ToggleButton
            key={option.value}
            id={`${name}-${option.value}`}
            value={option.value}
            variant="dark"
            className="segmented-toggle"
          >
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Form.Group>
  );
}

// Click to filter, click the active chip again to clear.
const ChipRow = ({
  label,
  options,
  value,
  onChange
}: {
  label: string;
  options: readonly string[];
  value: string;
  onChange: (value: string) => void;
}) => (
  <div className="market-chip-row">
    <div className="market-chip-label">{label}</div>
    <div className="market-chips">
      {options.map(option => (
        <button
          key={option}
          type="button"
          className={`market-chip${value === option ? " market-chip--active" : ""}`}
          aria-pressed={value === option}
          onClick={() => onChange(value === option ? "" : option)}
        >
          {option}
        </button>
      ))}
    </div>
  </div>
);

export const Market = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFilters = filtersFromSearchParams(searchParams);
  const initialPage = Number(searchParams.get("page")) || 1;

  const [filtersInput, setFiltersInput] = useState<FilterState>(initialFilters);
  const [filters, setFilters] = useState<FilterState>(initialFilters);
  const [page, setPage] = useState(initialPage);
  const [listings, setListings] = useState<MarketListing[]>([]);
  const [pagination, setPagination] = useState<PaginationMetadata>(EMPTY_PAGINATION);
  const [summary, setSummary] = useState<MarketSummary | null>(null);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  // Which view `listings` belongs to, so a stale table from the other view is
  // never rendered under the new view's columns.
  const [loadedView, setLoadedView] = useState<MarketView | null>(null);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<string[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  // Manual refresh is rate-limited so it can't be hammered.
  const [refreshCooling, setRefreshCooling] = useState(false);
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>(loadSavedSearches);
  const [showFilters, setShowFilters] = useState(
    () => !!(initialFilters.category || initialFilters.slot)
  );
  const [now, setNow] = useState(() => Date.now());

  const view = filters.view;
  const sort = filters.sort || DEFAULT_SORT[view];

  useEffect(() => {
    if (!refreshCooling) return undefined;
    const timer = setTimeout(() => setRefreshCooling(false), REFRESH_COOLDOWN_MS);
    return () => clearTimeout(timer);
  }, [refreshCooling]);

  // Live data moves; History is a ledger and only changes on a new sale.
  useEffect(() => {
    if (view !== "live") return undefined;
    const interval = setInterval(() => {
      setRefreshKey(k => k + 1);
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [view]);

  // Keeps "Synced 12m ago" ticking between data refreshes.
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 30 * 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const API_HOST = process.env.REACT_APP_API;
    fetch(`${API_HOST}public/market/categories`)
      .then(res => res.json())
      .then(setCategories)
      .catch(err => console.error("Error fetching categories:", err));
  }, []);

  // Debounce text/number filters so we're not firing a request per
  // keystroke - buttons apply immediately via applyNow.
  useEffect(() => {
    const timeout = setTimeout(() => {
      setFilters(filtersInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(timeout);
  }, [filtersInput]);

  // Mirrors the active filters/page into the URL so a search can be
  // hotlinked - replace (not push) keeps every debounced update from
  // spamming browser history.
  useEffect(() => {
    const params = new URLSearchParams();
    for (const key of FILTER_KEYS) {
      if (filters[key] && filters[key] !== DEFAULT_FILTERS[key]) {
        params.set(key, filters[key]);
      }
    }
    if (page > 1 && filters.view === "history") {
      params.set("page", page.toString());
    }
    setSearchParams(params, { replace: true });
  }, [filters, page, setSearchParams]);

  useEffect(() => {
    const API_HOST = process.env.REACT_APP_API;
    const controller = new AbortController();

    const fetchListings = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filters.q) params.set("q", filters.q);
        if (filters.seller) params.set("seller", filters.seller);
        if (filters.category) params.set("category", filters.category);
        if (filters.slot) params.set("slot", filters.slot);
        if (filters.minLevel) params.set("minLevel", filters.minLevel);
        if (filters.maxLevel) params.set("maxLevel", filters.maxLevel);
        params.set("sort", sort);
        if (view === "live") {
          // Everything currently listed fits on one page, so Live has no
          // pagination.
          params.set("limit", LIVE_LIMIT.toString());
          params.set("status", "listed");
          if (filters.buyoutOnly) params.set("buyoutOnly", "true");
        } else {
          params.set("outcome", filters.outcome);
          if (filters.buyer) params.set("buyer", filters.buyer);
          if (filters.period) params.set("period", filters.period);
        }
        if (view === "history") params.set("page", page.toString());

        const response = await fetch(
          `${API_HOST}public/market?${params.toString()}`,
          { signal: controller.signal }
        );
        const data = await response.json();
        const fetched: MarketListing[] = data.listings ?? [];
        setListings(
          IS_LOCAL_API && view === "live" && page === 1
            ? withDevListings(fetched, filters, sort)
            : fetched
        );
        setPagination(data.pagination ?? EMPTY_PAGINATION);
        setSummary(data.summary ?? null);
        setLastSyncedAt(data.lastSyncedAt ?? null);
        setLoadedView(view);
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        console.error("Error fetching market listings:", err);
        // A local API that's down or erroring still gets the sample listings.
        setListings(
          IS_LOCAL_API && view === "live" && page === 1
            ? withDevListings([], filters, sort)
            : []
        );
        setPagination(EMPTY_PAGINATION);
        setSummary(null);
        setLoadedView(view);
      } finally {
        // Skip for an aborted (superseded) request - a newer request is
        // already in flight and already set loading=true; without this
        // guard, this stale request's finally can flip loading back to
        // false while the real, current request is still pending.
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };
    fetchListings();

    // Cancels the in-flight request if filters/page change again before it
    // resolves - without this, a slower earlier request (e.g. a broader,
    // partially-typed query) can resolve after a newer/narrower one and
    // overwrite its results, which looks like search "not really filtering".
    return () => controller.abort();
  }, [filters, sort, view, page, refreshKey]);

  // Button/chip changes apply straight away; typed fields go through
  // setFiltersInput and the debounce above.
  const applyNow = (patch: Partial<FilterState>) => {
    const next = { ...filtersInput, ...patch };
    setFiltersInput(next);
    setFilters(next);
    setPage(1);
  };

  const applyFilters = (next: FilterState) => {
    setFiltersInput(next);
    setFilters(next);
    setPage(1);
  };

  const switchView = (next: MarketView) => {
    if (next === view) return;
    // Each view has its own sorts, so go back to the new view's default.
    applyNow({ view: next, sort: "" });
  };

  const saveCurrentSearch = () => {
    const name = window.prompt("Name this search:");
    if (!name) return;
    const next = [
      ...savedSearches.filter(s => s.name !== name),
      { name, filters }
    ];
    setSavedSearches(next);
    localStorage.setItem(SAVED_SEARCHES_KEY, JSON.stringify(next));
  };

  const deleteSavedSearch = (name: string) => {
    const next = savedSearches.filter(s => s.name !== name);
    setSavedSearches(next);
    localStorage.setItem(SAVED_SEARCHES_KEY, JSON.stringify(next));
  };

  const syncedMs = lastSyncedAt ? new Date(lastSyncedAt).getTime() : null;
  const syncStale = syncedMs !== null && now - syncedMs > STALE_SYNC_MS;
  const activeFilterCount =
    (filtersInput.category ? 1 : 0) + (filtersInput.slot ? 1 : 0);

  return (
    <Container className={`market-view market-view--${view}`}>
      <div className="d-flex justify-content-between align-items-center">
        <Title text="Market" />
        <Dropdown align="end">
          <Dropdown.Toggle
            variant="outline-secondary"
            size="sm"
            className="d-inline-flex align-items-center gap-1"
          >
            <Bookmark size={14} />
            Saved searches
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item onClick={saveCurrentSearch}>
              Save current search
            </Dropdown.Item>
            {savedSearches.length > 0 && <Dropdown.Divider />}
            {savedSearches.map(saved => (
              <Dropdown.Item
                key={saved.name}
                className="d-flex justify-content-between align-items-center gap-2"
                onClick={() => applyFilters(saved.filters)}
              >
                {saved.name}
                <X
                  size={14}
                  onClick={e => {
                    e.stopPropagation();
                    deleteSavedSearch(saved.name);
                  }}
                />
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <div className="market-view-bar">
        <ToggleButtonGroup
          type="radio"
          name="market-view"
          value={view}
          onChange={(next: MarketView) => switchView(next)}
          className="market-view-switch"
        >
          <ToggleButton
            id="market-view-live"
            value="live"
            variant="dark"
            className="market-view-tab market-view-tab--live"
          >
            <Flame size={16} className="market-live-dot" />
            Live
          </ToggleButton>
          <ToggleButton
            id="market-view-history"
            value="history"
            variant="dark"
            className="market-view-tab market-view-tab--history"
          >
            <History size={16} />
            History
          </ToggleButton>
        </ToggleButtonGroup>

        {view === "live" ? (
          <div className="d-flex align-items-center gap-2 market-view-status">
            <span
              className={`market-sync${syncStale ? " market-sync--stale" : ""}`}
              title={
                syncedMs
                  ? `Last data from the mud: ${new Date(syncedMs).toLocaleString()}`
                  : "No sync recorded"
              }
            >
              {syncedMs ? `Synced ${formatAgo(syncedMs, now)}` : "Not synced yet"}
            </span>
            <span className="market-view-caption">
              {listings.length} listing{listings.length === 1 ? "" : "s"}
            </span>
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={loading || refreshCooling}
              title={refreshCooling ? "Refreshed - try again in a minute" : "Refresh"}
              className="d-inline-flex align-items-center justify-content-center p-2"
              onClick={() => {
                setRefreshKey(k => k + 1);
                setRefreshCooling(true);
              }}
            >
              <RefreshCw size={16} className={loading ? "market-refresh-spinning" : undefined} />
            </Button>
          </div>
        ) : (
          <span className="market-view-caption">Concluded auctions</span>
        )}
      </div>

      <Form onSubmit={e => e.preventDefault()} className="market-controls">
        <Row className="align-items-end mb-3 text-light g-3">
          <Col xs={12} md={4}>
            <Form.Group>
              <Form.Label>Item name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Search item name"
                value={filtersInput.q}
                onChange={e => setFiltersInput({ ...filtersInput, q: e.target.value })}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={view === "live" ? 3 : 2}>
            <Form.Group>
              <Form.Label>Seller</Form.Label>
              <Form.Control
                type="text"
                placeholder="Search seller"
                value={filtersInput.seller}
                onChange={e =>
                  setFiltersInput({ ...filtersInput, seller: e.target.value })
                }
              />
            </Form.Group>
          </Col>
          {view === "history" && (
            <Col xs={12} md={2}>
              <Form.Group>
                <Form.Label>Buyer</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Search buyer"
                  value={filtersInput.buyer}
                  onChange={e =>
                    setFiltersInput({ ...filtersInput, buyer: e.target.value })
                  }
                />
              </Form.Group>
            </Col>
          )}
          <Col xs={6} md={1}>
            <Form.Group>
              <Form.Label>Min lvl</Form.Label>
              <Form.Control
                type="number"
                value={filtersInput.minLevel}
                onChange={e =>
                  setFiltersInput({ ...filtersInput, minLevel: e.target.value })
                }
              />
            </Form.Group>
          </Col>
          <Col xs={6} md={1}>
            <Form.Group>
              <Form.Label>Max lvl</Form.Label>
              <Form.Control
                type="number"
                value={filtersInput.maxLevel}
                onChange={e =>
                  setFiltersInput({ ...filtersInput, maxLevel: e.target.value })
                }
              />
            </Form.Group>
          </Col>
          <Col xs={12} md="auto">
            <Button
              variant={showFilters || activeFilterCount ? "primary" : "outline-secondary"}
              className="d-inline-flex align-items-center gap-1"
              aria-expanded={showFilters}
              onClick={() => setShowFilters(s => !s)}
            >
              <SlidersHorizontal size={16} />
              Category &amp; slot
              {activeFilterCount > 0 && ` (${activeFilterCount})`}
            </Button>
          </Col>
        </Row>

        {showFilters && (
          <div className="market-chip-panel mb-3">
            <ChipRow
              label="Category"
              options={categories}
              value={filtersInput.category}
              onChange={category => applyNow({ category })}
            />
            <ChipRow
              label="Slot"
              options={MARKET_SLOTS}
              value={filtersInput.slot}
              onChange={slot => applyNow({ slot })}
            />
          </div>
        )}

        <div className="market-choice-row mb-3 text-light">
          {view === "history" && (
            <>
              <ChoiceGroup<Outcome>
                label="Outcome"
                name="market-outcome"
                value={filtersInput.outcome}
                options={[
                  { value: "sold", label: "Sold" },
                  { value: "expired", label: "Expired" },
                  { value: "both", label: "Both" }
                ]}
                onChange={outcome => applyNow({ outcome })}
              />
              <ChoiceGroup<Period>
                label="Concluded"
                name="market-period"
                value={filtersInput.period}
                options={[
                  { value: "24h", label: "24h" },
                  { value: "7d", label: "7d" },
                  { value: "30d", label: "30d" },
                  { value: "", label: "All time" }
                ]}
                onChange={period => applyNow({ period })}
              />
            </>
          )}
          <ChoiceGroup<string>
            label="Sort by"
            name={`market-sort-${view}`}
            value={filtersInput.sort || DEFAULT_SORT[view]}
            options={sortsFor(view).map(s => ({ ...s }))}
            onChange={next =>
              applyNow({ sort: next === DEFAULT_SORT[view] ? "" : next })
            }
          />
          {view === "live" && (
            <Form.Group className="market-choice-group">
              <Form.Label className="d-block">Filter</Form.Label>
              <Button
                variant="dark"
                className={`segmented-toggle${filtersInput.buyoutOnly ? " active" : ""}`}
                aria-pressed={!!filtersInput.buyoutOnly}
                onClick={() =>
                  applyNow({ buyoutOnly: filtersInput.buyoutOnly ? "" : "true" })
                }
              >
                Buyout only
              </Button>
            </Form.Group>
          )}
        </div>
      </Form>

      {view === "history" && summary && summary.soldCount > 0 && (
        <div className="market-summary">
          <strong>{summary.soldCount.toLocaleString()}</strong> sold
          {summary.totals.map(t => (
            <span key={t.currency ?? "none"}>
              <span className="market-summary-sep">·</span>
              <strong>{formatCompact(t.total)}</strong> {t.currency || ""}
            </span>
          ))}
        </div>
      )}

      {loadedView !== view ? (
        <MarketTableSkeleton view={view} />
      ) : (
        <div className={loading ? "market-table-refetching" : undefined}>
          <MarketTable
            view={view}
            data={listings}
            pagination={pagination}
            onPageChange={setPage}
          />
        </div>
      )}
    </Container>
  );
};

export default Market;
